<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useHost,
  watch,
} from 'vue';
import ChatPanel from './components/ChatPanel.vue';
import ChatLauncher from './components/ChatLauncher.vue';
import ChatTeaser from './components/ChatTeaser.vue';
import { loadFont, resolveTheme, themeVariables } from './helpers/theme';
import { createTranslator } from './helpers/i18n';
import { createApi } from './helpers/api';
import { createStorage } from './helpers/storage';
import { useConversation } from './composables/useConversation';
import { scriptOrigin } from './helpers/scriptOrigin';

const props = defineProps({
  websiteToken: { type: String, default: '' },
  baseUrl: { type: String, default: '' },
  // floating: launcher + panel over the page · inline: panel in the page flow · preview: static, offline
  mode: { type: String, default: 'floating' },
  // Per-embed theme overrides, as a JSON attribute or (from JS) an object.
  theme: { type: [String, Object], default: null },
  // preview only: the inbox fields the live component would fetch (website_name, avatar_url, …).
  appearance: { type: Object, default: null },
  startMessage: { type: String, default: '' },
  locale: { type: String, default: '' },
  open: { type: Boolean, default: false },
});

const host = useHost();
const isPreview = computed(() => props.mode === 'preview');
const isFloating = computed(() => props.mode === 'floating');
const baseUrl = computed(() => props.baseUrl || scriptOrigin);

const appearance = ref(props.appearance || {});
const loaded = ref(isPreview.value);
const isOpen = ref(props.mode !== 'floating' || props.open);
const teaserVisible = ref(false);
const prefersDark = ref(
  window.matchMedia?.('(prefers-color-scheme: dark)').matches || false
);
const panel = ref(null);

const overrides = computed(() => {
  if (!props.theme) return {};
  if (typeof props.theme === 'object') return props.theme;
  try {
    return JSON.parse(props.theme);
  } catch {
    return {};
  }
});

const chatTheme = computed(() =>
  resolveTheme(appearance.value, overrides.value)
);
const isDark = computed(
  () =>
    chatTheme.value.appearance === 'dark' ||
    (chatTheme.value.appearance === 'auto' && prefersDark.value)
);
const cssVariables = computed(() =>
  themeVariables(chatTheme.value, isDark.value)
);
const t = computed(() =>
  createTranslator(props.locale || appearance.value.locale || 'pt_BR')
);
const launcherLabel = computed(
  () => chatTheme.value.launcher_label || t.value('LAUNCHER_DEFAULT')
);
const subtitle = computed(() => {
  if (chatTheme.value.subtitle) return chatTheme.value.subtitle;
  const replyTime = (
    appearance.value.reply_time || 'in_a_few_minutes'
  ).toUpperCase();
  return t.value(`REPLY_TIME.${replyTime}`);
});
const whatsappUrl = computed(() => {
  const digits = String(chatTheme.value.whatsapp_number || '').replace(
    /\D/g,
    ''
  );
  return digits ? `https://wa.me/${digits}` : '';
});

watch(() => chatTheme.value.font_family, loadFont, { immediate: true });
watch(
  () => props.appearance,
  value => {
    if (isPreview.value && value) appearance.value = value;
  }
);

const emitHostEvent = (name, detail = {}) =>
  host?.dispatchEvent(
    new CustomEvent(`arkemis-chat:${name}`, {
      detail,
      bubbles: true,
      composed: true,
    })
  );

// ---- live conversation (everything below is skipped in preview) ----
const api = props.websiteToken
  ? createApi({ baseUrl: baseUrl.value, websiteToken: props.websiteToken })
  : null;
const storage = props.websiteToken ? createStorage(props.websiteToken) : null;
const conversation = api
  ? useConversation({
      api,
      storage,
      wsUrl: `${baseUrl.value.replace(/^http/, 'ws')}/cable`,
      isOpen,
    })
  : null;

const previewChoices = ref(null);
const previewMessages = computed(() => {
  const menu = {
    id: 'preview-menu',
    kind: 'agent',
    content: t.value('PREVIEW.QUESTION'),
    contentType: 'input_select',
    items: [1, 2, 3].map(n => ({
      title: t.value(`PREVIEW.OPTION_${n}`),
      value: `option-${n}`,
    })),
    submitted: previewChoices.value,
    attachments: [],
  };
  return [
    {
      id: 'preview-greeting',
      kind: 'agent',
      content: t.value('PREVIEW.GREETING'),
      attachments: [],
    },
    menu,
  ];
});

const panelState = computed(() => {
  if (isPreview.value) {
    const messages = previewMessages.value;
    return {
      status: 'ready',
      messages,
      choices: messages[1].submitted ? null : messages[1],
      typing: false,
    };
  }
  return {
    status: conversation.status,
    messages: conversation.visibleMessages,
    choices:
      conversation.status === 'ready' ? conversation.activeChoices : null,
    typing: conversation.awaitingReply || conversation.agentTyping,
  };
});

const startConversation = async () => {
  if (!conversation) return;
  try {
    await conversation.connect();
    // The composer only renders once connected, so the focus from openChat had nothing to land on.
    await nextTick();
    if (isOpen.value) panel.value?.focus();
    await conversation.startIfEmpty(
      props.startMessage || chatTheme.value.start_message
    );
    conversation.markSeen();
  } catch {
    // status is now 'error'; the panel offers a retry
  }
};

const lockPageScroll = locked => {
  if (!isFloating.value) return;
  const small = window.matchMedia('(max-width: 639px)').matches;
  document.documentElement.style.overflow = locked && small ? 'hidden' : '';
};

const openChat = async () => {
  if (isOpen.value && isFloating.value) return;
  isOpen.value = true;
  teaserVisible.value = false;
  if (conversation) conversation.unread = 0;
  lockPageScroll(true);
  emitHostEvent('open');
  await nextTick();
  panel.value?.focus();
  if (!isPreview.value) startConversation();
};

const closeChat = () => {
  if (!isFloating.value) return;
  isOpen.value = false;
  lockPageScroll(false);
  emitHostEvent('close');
};

const toggleChat = () => (isOpen.value ? closeChat() : openChat());

const send = content => {
  if (isPreview.value) return;
  conversation.send(content);
  emitHostEvent('message', { content });
};

const choose = (message, option) => {
  if (isPreview.value) {
    previewChoices.value = { title: option.title, value: option.value };
    setTimeout(() => {
      previewChoices.value = null;
    }, 1800);
    return;
  }
  conversation.choose(message, option);
  emitHostEvent('message', { content: option.title, choice: option.value });
};

const dismissTeaser = () => {
  teaserVisible.value = false;
  storage?.setSession({ teaserDismissed: true });
};

// ---- host page integration ----
const onCommand = event => {
  const action = { open: openChat, close: closeChat, toggle: toggleChat }[
    event.detail
  ];
  action?.();
};
const onKeydown = event => {
  if (event.key === 'Escape' && isOpen.value && isFloating.value) closeChat();
};
const darkQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
const onSchemeChange = event => {
  prefersDark.value = event.matches;
};
let teaserTimer = null;

onMounted(async () => {
  host?.addEventListener('arkemis-chat:command', onCommand);
  document.addEventListener('keydown', onKeydown);
  darkQuery?.addEventListener?.('change', onSchemeChange);
  if (isPreview.value) return;
  if (!api) {
    // eslint-disable-next-line no-console
    console.error('<arkemis-chat>: the website-token attribute is required');
    return;
  }

  try {
    appearance.value = await api.appearance();
  } finally {
    loaded.value = true;
  }

  // A returning visitor sees their history right away in inline mode; floating waits for a click.
  if (props.mode === 'inline' && storage.get().authToken) startConversation();
  if (isOpen.value && isFloating.value) startConversation();

  const { teaser_text: teaserText, teaser_delay: delay } = chatTheme.value;
  if (isFloating.value && teaserText && !storage.session().teaserDismissed) {
    teaserTimer = setTimeout(() => {
      if (!isOpen.value) teaserVisible.value = true;
    }, delay * 1000);
  }
});

onBeforeUnmount(() => {
  clearTimeout(teaserTimer);
  host?.removeEventListener('arkemis-chat:command', onCommand);
  document.removeEventListener('keydown', onKeydown);
  darkQuery?.removeEventListener?.('change', onSchemeChange);
  conversation?.disconnect();
  lockPageScroll(false);
});

const dockStyle = computed(() => ({
  [chatTheme.value.position === 'left' ? 'left' : 'right']:
    `${chatTheme.value.offset_x}px`,
  bottom: `${chatTheme.value.offset_y}px`,
}));
const alignClass = computed(() =>
  chatTheme.value.position === 'left' ? 'items-start' : 'items-end'
);
</script>

<template>
  <div
    :style="cssVariables"
    class="font-sans"
    :class="mode === 'floating' ? '' : 'h-full'"
  >
    <!-- floating -->
    <div
      v-if="isFloating && loaded"
      class="pointer-events-none fixed z-[2147483000] flex flex-col gap-4"
      :class="alignClass"
      :style="dockStyle"
    >
      <Transition
        enter-active-class="transition duration-300 ease-[cubic-bezier(0.2,0.9,0.3,1.05)] motion-reduce:transition-none"
        enter-from-class="translate-y-4 scale-[0.97] opacity-0"
        leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
        leave-to-class="translate-y-2 scale-[0.98] opacity-0"
      >
        <div
          v-if="isOpen"
          class="pointer-events-auto h-[min(680px,calc(100vh-7.5rem))] w-[392px] origin-bottom overflow-hidden rounded-ark-lg shadow-panel max-sm:fixed max-sm:inset-0 max-sm:h-auto max-sm:w-auto max-sm:rounded-none"
          :class="
            chatTheme.position === 'left'
              ? 'origin-bottom-left'
              : 'origin-bottom-right'
          "
          role="dialog"
          :aria-label="chatTheme.title"
        >
          <ChatPanel
            ref="panel"
            v-bind="panelState"
            :theme="chatTheme"
            :subtitle="subtitle"
            :whatsapp-url="whatsappUrl"
            :t="t"
            @close="closeChat"
            @send="send"
            @choose="choose"
            @retry="conversation.retry"
            @start="startConversation"
          />
        </div>
      </Transition>

      <!-- Teaser beside the launcher on desktop, stacked above it on phones where the row would overflow. -->
      <div
        class="pointer-events-auto flex gap-3 max-sm:flex-col"
        :class="[
          chatTheme.position === 'left'
            ? 'flex-row-reverse items-end max-sm:items-start'
            : 'items-end',
          isOpen ? 'max-sm:hidden' : '',
        ]"
      >
        <ChatTeaser
          v-if="teaserVisible && !isOpen"
          :theme="chatTheme"
          :text="chatTheme.teaser_text"
          :t="t"
          @open="openChat"
          @dismiss="dismissTeaser"
        />
        <ChatLauncher
          :theme="chatTheme"
          :is-open="isOpen"
          :unread="conversation?.unread || 0"
          :label="launcherLabel"
          :t="t"
          @toggle="toggleChat"
        />
      </div>
    </div>

    <!-- inline: sits in the page, so there is nothing to close -->
    <div
      v-else-if="mode === 'inline' && loaded"
      class="h-full overflow-hidden rounded-ark-lg border border-line shadow-sm"
    >
      <ChatPanel
        ref="panel"
        v-bind="panelState"
        :theme="chatTheme"
        :subtitle="subtitle"
        :whatsapp-url="whatsappUrl"
        :closable="false"
        :t="t"
        @send="send"
        @choose="choose"
        @retry="conversation.retry"
        @start="startConversation"
      />
    </div>

    <!-- preview: the dashboard's live mock-up of the floating layout -->
    <div
      v-else-if="isPreview"
      class="flex h-full flex-col gap-4 p-5"
      :class="alignClass"
    >
      <div
        class="min-h-0 w-full max-w-[392px] flex-1 overflow-hidden rounded-ark-lg shadow-panel"
      >
        <ChatPanel
          v-bind="panelState"
          :theme="chatTheme"
          :subtitle="subtitle"
          :whatsapp-url="whatsappUrl"
          :t="t"
          @choose="choose"
        />
      </div>
      <!-- The settings column is too narrow for teaser and launcher side by side, so it shows them stacked. -->
      <div class="flex flex-col gap-3" :class="alignClass">
        <ChatTeaser
          v-if="chatTheme.teaser_text"
          :theme="chatTheme"
          :text="chatTheme.teaser_text"
          :t="t"
        />
        <ChatLauncher
          :theme="chatTheme"
          :label="launcherLabel"
          :unread="chatTheme.teaser_text ? 1 : 0"
          :t="t"
        />
      </div>
    </div>
  </div>
</template>
